import { FC, useCallback, useEffect, useRef } from 'react';

import { useCameraPreview } from '../../../hooks/useCameraPreview';
import Button from '../../common/button';
import { VideoIcon } from '../../icons/video';

export interface GroupClientLobbyProps {
  /** Психолог уже разрешил вход — показываем кнопку «Войти» и автоматически запускаем вход */
  connectionAllowed: boolean;
  /** Состояние камеры с экрана «Запросить разрешение» */
  initialCameraEnabled?: boolean;
  onEnterMeeting: (isCameraEnabled: boolean) => void;
}

/**
 * Экран ожидания для клиента групповой терапии: после «Запросить разрешение»
 * клиент видит этот экран до разрешения психолога; после разрешения — автоматический вход на встречу (или кнопка «Войти»).
 */
export const GroupClientLobby: FC<GroupClientLobbyProps> = ({
  connectionAllowed,
  initialCameraEnabled = true,
  onEnterMeeting,
}) => {
  const wasAllowedRef = useRef(false);
  const { videoRef, isLoading, cameraError, isCameraEnabled, toggleCamera, stopStream } =
    useCameraPreview(initialCameraEnabled);
  const isCameraEnabledRef = useRef(isCameraEnabled);
  isCameraEnabledRef.current = isCameraEnabled;

  const enterMeeting = useCallback(() => {
    stopStream();
    onEnterMeeting(isCameraEnabledRef.current);
  }, [onEnterMeeting, stopStream]);

  useEffect(() => {
    if (connectionAllowed && !wasAllowedRef.current) {
      wasAllowedRef.current = true;
      enterMeeting();
    }
    if (!connectionAllowed) {
      wasAllowedRef.current = false;
    }
  }, [connectionAllowed, enterMeeting]);

  return (
    <div className="fixed inset-0 w-screen h-screen min-w-full min-h-full flex flex-col bg-[#151324]">
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-[344px] sm:max-w-[390px] mb-6 rounded-2xl overflow-hidden bg-gray-900 relative aspect-video shadow-md">
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover -scale-x-100"
          />

          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
              <span className="animate-spin w-7 h-7 border-2 border-white border-t-transparent rounded-full" />
            </div>
          )}

          {cameraError && !isLoading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900 text-white text-center px-4 gap-2">
              <VideoIcon checked width={40} height={40} />
              <p className="text-xs leading-relaxed">{cameraError}</p>
            </div>
          )}

          {!isCameraEnabled && !cameraError && !isLoading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900 text-white gap-2">
              <VideoIcon checked width={40} height={40} />
              <p className="text-xs">Камера выключена</p>
            </div>
          )}

          <div className="absolute bottom-2 left-2 flex gap-2">
            <button
              type="button"
              onClick={toggleCamera}
              disabled={!!cameraError}
              className="disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <VideoIcon checked={!isCameraEnabled} width={36} height={36} />
            </button>
          </div>
        </div>

        {!connectionAllowed ? (
          <>
            <div className="w-10 h-10 border-2 border-white border-t-redBase rounded-full animate-spin mb-6" />
            <p className="text-white text-center text-lg font-semibold mb-2 font-comfortaa">Подключение к встрече</p>
            <p className="text-white text-center text-sm  font-comfortaa">
              Подождите, пока организатор примет ваш запрос
            </p>
          </>
        ) : (
          <>
            <p className="text-white text-center text-lg mb-6">Психолог разрешил вам войти на встречу.</p>
            <Button
              className="!w-[344px] sm:!w-[390px] max-w-[90%] p-[15px_20px] md:p-[15px_60px]"
              onClick={enterMeeting}
            >
              Войти
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
